/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 애니메이션 프레임을 관리한다.
 *
 * @file /src/utils/AnimationFrames.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 27.
 */
import { UUID } from '@utils/UUID';
import { Exceptions } from '@utils/Exceptions';

interface FrameLoopRecord {
    uuid: string;
    label: string;
    startTime: number;
    endTime?: number;
    frameCount: number;
    active: boolean;
    running: boolean;
    pendingLoop: boolean;
    rAFId?: number;
    callback?: (time: DOMHighResTimeStamp) => boolean;
}

export class AnimationFrames {
    private static animationFrames: Map<string, FrameLoopRecord> = new Map();

    /**
     * 애니메이션 프레임을 요청한다.
     *
     * @param callback - 콜백 함수 (async 가능)
     * @param label - 디버그용 라벨 (없으면 'noname')
     * @param contextOrUuid - context (this) 또는 uuid (내부용)
     */
    static request(callback: (time: DOMHighResTimeStamp) => boolean, label?: string, contextOrUuid?: any): string {
        let uuid: string;
        let context: any = undefined;
        label ??= 'noname';

        if (typeof contextOrUuid === 'string') {
            uuid = contextOrUuid;
        } else {
            context = contextOrUuid;
            uuid = UUID.v4();
        }

        const boundCallback = context ? callback.bind(context) : callback;

        let record = this.animationFrames.get(uuid);
        if (record === undefined) {
            record = {
                uuid,
                label: label,
                startTime: performance.now(),
                frameCount: 0,
                active: false,
                running: false,
                pendingLoop: false,
                callback: boundCallback,
            };
            this.animationFrames.set(uuid, record);
        }

        this.startFrame(record);
        return uuid;
    }

    /**
     * 수동으로 다음 frame 을 예약한다. (수동 loop 지원)
     *
     * @param uuid - loop 할 uuid
     */
    static loop(uuid: string): void {
        const record = this.animationFrames.get(uuid);
        if (record === undefined) {
            Exceptions.warn('[AnimationFrames]', `Cannot loop unknown UUID: ${uuid}`);
            return;
        }

        if (record.running == true) {
            record.pendingLoop = true;
            return;
        }

        if (record.active == true) {
            return;
        }

        this.startFrame(record);
    }

    /**
     * AF 를 시작한다.
     *
     * @param record - 시작할 AF 레코드
     */
    private static startFrame(record: FrameLoopRecord): void {
        if (record.active == true) {
            return;
        }

        record.active = true;
        record.pendingLoop = false;

        const rAFId = requestAnimationFrame((time: DOMHighResTimeStamp) => {
            record!.frameCount += 1;

            try {
                record.active = false;
                record.running = true;
                const result = record.callback(time);
                record.running = false;

                if (record.pendingLoop == true || result === true) {
                    this.startFrame(record);
                } else {
                    record!.endTime = performance.now();
                }
            } catch (e) {
                Exceptions.error('[AnimationFrames]', `Error in callback [${record.label}]`, e);
                record!.running = false;
                record!.pendingLoop = false;
                record!.endTime = performance.now();
            }
        });

        record.rAFId = rAFId;
    }

    /**
     * 특정 UUID AF 가 현재 활성화중인지 확인한다.
     *
     * @param uuid - 활성화여부를 확인할 UUID
     * @returns 활성화여부
     */
    static isActive(uuid: string): boolean {
        return this.animationFrames.get(uuid)?.active === true;
    }

    /**
     * 특정 UUID AF 를 중단한다.
     *
     * @param uuid - 중단할 UUID
     * @param is_remove - 기록삭제여부
     */
    static cancel(uuid: string, is_remove: boolean = false): void {
        const record = this.animationFrames.get(uuid);
        if (record === undefined) {
            return;
        }

        if (typeof record.rAFId == 'number') {
            cancelAnimationFrame(record.rAFId);
        }

        if (is_remove == true) {
            this.animationFrames.delete(uuid);
        } else {
            record.active = false;
            record.endTime = performance.now();
            record.callback = undefined;
        }

        if (record.active == true) {
            Exceptions.debug('[AnimationFrames]', `Canceled frame [${record.label}] UUID: ${record.uuid}`);
        }
    }

    /**
     * 현재 활성화중인 AF 를 가져온다.
     *
     * @returns 활성화중인 AF 레코드
     */
    static getActiveFrames(): FrameLoopRecord[] {
        return Array.from(this.animationFrames.values()).filter((record) => record.active);
    }

    /**
     * 현재 활성화중인 AF 갯수를 가져온다.
     *
     * @returns 활성화중인 AF 갯수
     */
    static getActiveCount(): number {
        return this.getActiveFrames().length;
    }

    /**
     * 전체 AF 갯수를 가져온다.
     *
     * @returns 전체 AF 갯수
     */
    static getTotalCount(): number {
        return this.animationFrames.size;
    }

    /**
     * 디버그 데이터를 콘솔에 출력한다.
     */
    static debug(): void {
        Exceptions.debug('[AnimationFrames]', `Total Loops: ${this.animationFrames.size}`);

        const tableData: any[] = [];

        for (const record of this.animationFrames.values()) {
            const duration = (record.endTime ?? performance.now()) - record.startTime;
            const fps = (record.frameCount / duration) * 1000;
            const spf = duration / record.frameCount;

            tableData.push({
                UUID: record.uuid,
                Label: record.label,
                Active: record.active,
                Frames: record.frameCount,
                Duration: duration.toFixed(2) + 'ms / ' + spf.toFixed(2) + 'ms/F',
                FPS: fps.toFixed(1),
            });
        }

        Exceptions.table(tableData);
    }

    /**
     * 모든 AF 를 종료한다.
     */
    static terminateAll(): void {
        for (const record of this.animationFrames.values()) {
            if (record.active == true) {
                if (typeof record.rAFId === 'number') {
                    cancelAnimationFrame(record.rAFId);
                }
                record.active = false;
                record.endTime = performance.now();
            }
        }
    }
}
